import React, { useState } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Card,
  Box,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Pagination,
  InputAdornment,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  FaArrowsAltV,
  FaLongArrowAltUp,
  FaLongArrowAltDown,
} from 'react-icons/fa'
import theme from '../../theme'

const headerTint = alpha(theme.palette.primary.main, 0.07)
const zebraTint = alpha(theme.palette.primary.main, 0.03)
const hoverTint = alpha(theme.palette.primary.main, 0.08)

export type DataTableProps<TData> = {
  data: TData[]
  columns: ColumnDef<TData, any>[]
  renderSubComponent?: (row: any) => React.ReactNode
  manualPagination?: boolean
  pageCount?: number
  totalCount?: number
  onPaginationChange?: (pagination: {
    pageIndex: number
    pageSize: number
  }) => void
  initialPagination?: { pageIndex: number; pageSize: number }
  globalFilter?: string
  onGlobalFilterChange?: (filter: string) => void
  showSearchButton?: boolean
  onSearchSubmit?: () => void
  showGlobalSearch?: boolean
  showColumnFilters?: boolean
  showPagination?: boolean
  toolbarEnd?: React.ReactNode
}

export type TableColumnMeta = {
  align?: 'left' | 'center' | 'right'
}

export type GenericColumnDef<TData> = ColumnDef<TData, any> & {
  meta?: TableColumnMeta
}

export function DataTable<TData>({
  data,
  columns,
  renderSubComponent,
  manualPagination = false,
  pageCount,
  totalCount,
  onPaginationChange,
  initialPagination = { pageIndex: 0, pageSize: 30 },
  globalFilter: externalGlobalFilter,
  onGlobalFilterChange: externalOnGlobalFilterChange,
  showSearchButton = false,
  onSearchSubmit,
  showGlobalSearch = true,
  showColumnFilters = true,
  showPagination = true,
  toolbarEnd,
}: DataTableProps<TData>) {
  const [localGlobalFilter, setLocalGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<any>([])
  const [pagination, setPagination] = useState(initialPagination)

  const isGlobalFilterControlled = externalGlobalFilter !== undefined
  const globalFilter = isGlobalFilterControlled ? externalGlobalFilter : localGlobalFilter

  const handleGlobalFilterChange = (value: string) => {
    if (isGlobalFilterControlled) {
      if (externalOnGlobalFilterChange) {
        externalOnGlobalFilterChange(value)
      }
    } else {
      setLocalGlobalFilter(value)
    }
  }

  const handlePaginationChange = (updater: any) => {
    const nextPagination =
      typeof updater === 'function' ? updater(pagination) : updater
    setPagination(nextPagination)
    if (onPaginationChange) {
      onPaginationChange(nextPagination)
    }
  }

  // When pagination is hidden, recompute the effective page size from the
  // current data length every render (rather than a fixed initial value) so
  // a later refetch that changes the row count can never silently truncate
  // the list -- there are no visible controls to reveal a hidden page.
  const effectivePagination = showPagination
    ? pagination
    : { pageIndex: 0, pageSize: Math.max(data.length, 1) }

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, sorting, pagination: effectivePagination },
    onGlobalFilterChange: handleGlobalFilterChange,
    onSortingChange: setSorting,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!renderSubComponent,
    getRowId: (row: any) => row.id,
    manualPagination,
    manualFiltering: manualPagination,
    pageCount,
  })

  const { pageIndex, pageSize } = table.getState().pagination
  const rowsTotal = manualPagination
    ? (totalCount ?? (pageCount ?? 0) * pageSize)
    : table.getFilteredRowModel().rows.length
  const rangeStart = rowsTotal === 0 ? 0 : pageIndex * pageSize + 1
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, rowsTotal)

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        boxShadow: '0 1px 2px rgba(18,54,64,0.06), 0 4px 18px rgba(18,54,64,0.07)',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      {(showGlobalSearch || toolbarEnd) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          {showGlobalSearch && (
            <>
              <TextField
                size="small"
                placeholder="Search all columns..."
                value={globalFilter ?? ''}
                onChange={(e) => handleGlobalFilterChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && onSearchSubmit) {
                    e.preventDefault()
                    onSearchSubmit()
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: 380,
                  flex: '0 1 380px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 100,
                    bgcolor: 'background.paper',
                    fontSize: '0.95rem',
                  },
                }}
              />
              {showSearchButton && (
                <Button
                  variant="contained"
                  onClick={onSearchSubmit}
                  sx={{ borderRadius: 100, textTransform: 'none', px: 2.5, fontSize: '0.9rem' }}
                >
                  Search
                </Button>
              )}
            </>
          )}
          {toolbarEnd && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              {toolbarEnd}
            </Box>
          )}
        </Box>
      )}

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead sx={{ bgcolor: headerTint }}>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    sx={{
                      cursor: header.column.getCanSort()
                        ? 'pointer'
                        : 'default',
                      whiteSpace: 'nowrap',
                      width: header.column.getSize(),
                      py: 1.5,
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      color: header.column.getIsSorted()
                        ? 'primary.dark'
                        : 'text.secondary',
                      borderColor: 'divider',
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}

                    {header.column.getCanSort() && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          display: 'inline-flex',
                          verticalAlign: 'middle',
                          opacity: header.column.getIsSorted() ? 1 : 0.35,
                        }}
                      >
                        {{
                          asc: (
                            <FaLongArrowAltUp
                              color={theme.palette.primary.main}
                            />
                          ),
                          desc: (
                            <FaLongArrowAltDown
                              color={theme.palette.primary.main}
                            />
                          ),
                        }[header.column.getIsSorted() as string] ?? (
                          <FaArrowsAltV color={theme.palette.text.secondary} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* Column Filters */}
            {showColumnFilters && (
              <TableRow>
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{ py: 1, borderColor: 'divider' }}
                  >
                    {header.column.getCanFilter() ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Filter..."
                        value={
                          (header.column.getFilterValue() as string) ?? ''
                        }
                        onChange={(e) =>
                          header.column.setFilterValue(e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterAltOutlinedIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          maxWidth: 170,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'background.paper',
                            borderRadius: 1.5,
                            fontSize: '0.88rem',
                            height: 38,
                          },
                        }}
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    onClick={row.getToggleExpandedHandler()}
                    sx={{
                      cursor: renderSubComponent ? 'pointer' : 'default',
                      transition: 'background-color 0.1s',
                      '&:nth-of-type(even)': { bgcolor: zebraTint },
                      '&:hover': {
                        bgcolor: hoverTint,
                        boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        align={
                          (cell.column.columnDef.meta as any)?.align ===
                          'right'
                            ? 'right'
                            : 'left'
                        }
                        sx={{ py: 1.5, borderColor: 'divider', fontSize: '0.95rem' }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>

                  {row.getIsExpanded() && renderSubComponent && (
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ p: 0 }}>
                        {renderSubComponent(row)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ color: 'text.secondary', py: 4 }}
                >
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
            Showing <strong>{rangeStart}–{rangeEnd}</strong> of{' '}
            <strong>{rowsTotal}</strong>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Pagination
              shape="rounded"
              count={table.getPageCount()}
              page={table.getState().pagination.pageIndex + 1}
              onChange={(_, page) => table.setPageIndex(page - 1)}
              color="primary"
              sx={{ '& .MuiPaginationItem-root': { fontSize: '0.95rem' } }}
            />
            <TextField
              select
              size="small"
              SelectProps={{ native: true }}
              value={table.getState().pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              sx={{
                minWidth: 120,
                '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.9rem' },
              }}
            >
              {[30, 50, 100, 150].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </TextField>
          </Box>
        </Box>
      )}
    </Card>
  )
}
